import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Paper,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  CheckCircle,
  DeleteOutline as DeleteOutlineIcon,
  AttachFile,
} from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { head } from "lodash";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(2),
  },
  step: {
    "& .MuiStepLabel-label": {
      fontSize: "1rem",
    },
  },
  input: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    width: "100%",
  },
  addButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  attachmentPreview: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.type === "dark" ? "#424242" : "#f5f5f5",
    borderRadius: 4,
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
}));

// Função auxiliar para criar nova opção
const createNewOption = (queueId, parentId = null, optionNumber = 1) => ({
  title: "",
  message: "",
  edition: false,
  option: optionNumber,
  queueId,
  parentId,
  children: [],
  mediaPath: null,
  mediaName: null,
});

// Componente recursivo principal
export function QueueOptionStepper({ queueId, options, onUpdate }) {
  const classes = useStyles();
  const [localOptions, setLocalOptions] = useState(options);

  // ✅ Sempre sincroniza com o props 'options' (mesmo após deletar no backend)
  useEffect(() => {
    setLocalOptions(JSON.parse(JSON.stringify(options))); // Deep clone evita referência
  }, [options]);

  const handleChange = (index, field, value) => {
    const newOptions = [...localOptions];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setLocalOptions(newOptions);
  };

  const toggleEdition = (index) => {
    const newOptions = [...localOptions];
    newOptions[index] = { ...newOptions[index], edition: !newOptions[index].edition };
    setLocalOptions(newOptions);
  };

  const handleSave = async (index) => {
    const option = localOptions[index];
    if (!option.title || !option.title.trim()) {
      alert("O título da opção é obrigatório.");
      return;
    }

    try {
      let savedOption;
      if (option.id) {
        const { data } = await api.put(`/queue-options/${option.id}`, option);
        savedOption = data;
      } else {
        const { data } = await api.post("/queue-options", option);
        savedOption = data;
      }

      const updatedOptions = [...localOptions];
      updatedOptions[index] = { ...savedOption, edition: false, children: option.children };
      setLocalOptions(updatedOptions);

      // ✅ Notifica o pai com cópia limpa
      if (onUpdate) onUpdate([...updatedOptions]);
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async (index) => {
    const option = localOptions[index];

    if (option.id) {
      try {
        await api.delete(`/queue-options/${option.id}`);
      } catch (err) {
        toastError(err);
        return;
      }
    }

    // ✅ Remove sem mutação
    const filtered = localOptions.filter((_, i) => i !== index);
    const reordered = filtered.map((opt, i) => ({ ...opt, option: i + 1 }));

    setLocalOptions(reordered);

    // ✅ Notifica o pai IMEDIATAMENTE
    if (onUpdate) onUpdate(reordered);
  };

  const handleAddChild = (index) => {
    const parent = localOptions[index];
    const newOption = createNewOption(queueId, parent.id, parent.children.length + 1);
    const updatedOptions = [...localOptions];
    updatedOptions[index] = {
      ...parent,
      children: [...parent.children, newOption],
    };
    setLocalOptions(updatedOptions);
    if (onUpdate) onUpdate(updatedOptions); // ✅ propaga mudança
  };

  const handleUpload = async (file, optionId) => {
    if (!file || !optionId) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/queue-options/${optionId}/media-upload`, formData);
    } catch (err) {
      toastError(err);
    }
  };

  // ✅ MediaUpload interno
  function MediaUpload({ initialPath, initialName, onUpload, onDelete }) {
    const [file, setFile] = useState(null);
    const fileInputRef = React.useRef(null);

    const handleFileChange = (e) => {
      const uploadedFile = head(e.target.files);
      if (uploadedFile) {
        setFile(uploadedFile);
        onUpload(uploadedFile);
      }
    };

    const removeFile = () => {
      if (file || initialPath) {
        if (onDelete) onDelete();
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };

    const fileName = file ? file.name : initialName;

    return (
      <Grid container spacing={1} alignItems="center">
        {!fileName ? (
          <Grid item>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <Button
              size="small"
              startIcon={<AttachFile />}
              onClick={() => fileInputRef.current.click()}
            >
              Anexar Arquivo
            </Button>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Paper variant="outlined" className={classes.attachmentPreview} elevation={0}>
              <Typography variant="body2" noWrap color="textPrimary" style={{ flex: 1 }}>
                {fileName}
              </Typography>
              <IconButton size="small" color="secondary" onClick={removeFile}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Paper>
          </Grid>
        )}
      </Grid>
    );
  }

  const renderTitle = (option, index) => {
    if (option.edition) {
      return (
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12}>
            <TextField
              label="Título da Opção"
              value={option.title}
              onChange={(e) => handleChange(index, "title", e.target.value)}
              size="small"
              className={classes.input}
              autoFocus
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Mensagem"
              multiline
              rows={2}
              value={option.message}
              onChange={(e) => handleChange(index, "message", e.target.value)}
              size="small"
              className={classes.input}
            />
          </Grid>

          <Grid item xs={12}>
            <MediaUpload
              initialPath={option.mediaPath}
              initialName={option.mediaName}
              onUpload={(file) => handleUpload(file, option.id)}
              onDelete={async () => {
                if (option.id) {
                  await api.delete(`/queue-options/${option.id}/media-upload`);
                }
              }}
            />
          </Grid>

          <Grid item>
            <IconButton size="small" color="primary" onClick={() => handleSave(index)}>
              <CheckCircle fontSize="small" />
            </IconButton>
          </Grid>
          <Grid item>
            <IconButton size="small" color="secondary" onClick={() => handleDelete(index)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      );
    }

    return (
      <Typography variant="body1" style={{ fontWeight: 500 }}>
        {option.title || "(Sem título)"}
        <IconButton size="small" onClick={() => toggleEdition(index)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Typography>
    );
  };

  const renderChildren = (option, index) => {
    if (!option.children || option.children.length === 0) return null;

    return (
      <QueueOptionStepper
        queueId={queueId}
        options={option.children}
        onUpdate={(children) => {
          const newOptions = JSON.parse(JSON.stringify(localOptions)); // Deep clone
          newOptions[index].children = children;
          setLocalOptions(newOptions);
          if (onUpdate) onUpdate(newOptions);
        }}
      />
    );
  };

  return (
    <Stepper orientation="vertical" nonLinear>
      {localOptions.map((option, index) => (
        <Step key={option.id ? `opt-${option.id}` : `new-${index}-${option.option}`} className={classes.step}>
          <StepLabel onClick={() => !option.edition && toggleEdition(index)} style={{ cursor: "pointer" }}>
            {renderTitle(option, index)}
          </StepLabel>
          <StepContent>
            {option.id !== undefined && (
              <Button
                startIcon={<AddIcon />}
                size="small"
                color="primary"
                variant="outlined"
                className={classes.addButton}
                onClick={() => handleAddChild(index)}
              >
                Adicionar Subopção
              </Button>
            )}
            {renderChildren(option, index)}
          </StepContent>
        </Step>
      ))}
    </Stepper>
  );
}

// Componente raiz
export function QueueOptions({ queueId }) {
  const classes = useStyles();
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (!queueId) return;
    const fetchOptions = async () => {
      try {
        const { data } = await api.get("/queue-options", {
          params: { queueId, parentId: -1 },
        });
        const formatted = data.map((opt) => ({
          ...opt,
          children: [],
          edition: false,
        }));
        setOptions(formatted);
      } catch (err) {
        toastError(err);
      }
    };
    fetchOptions();
  }, [queueId]);

  const addRootOption = () => {
    const newOpt = createNewOption(queueId, null, options.length + 1);
    setOptions((prev) => [...prev, newOpt]);
  };

  const handleUpdate = (updatedOptions) => {
    // ✅ Força nova referência para garantir rerender
    setOptions(JSON.parse(JSON.stringify(updatedOptions)));
  };

  if (!queueId) return null;

  return (
    <div className={classes.root}>
      <Typography variant="h6">Menu de Opções</Typography>
      <Button
        startIcon={<AddIcon />}
        size="small"
        color="primary"
        variant="outlined"
        className={classes.addButton}
        onClick={addRootOption}
      >
        Nova Opção
      </Button>

      {options.length > 0 ? (
        <QueueOptionStepper
          queueId={queueId}
          options={options}
          onUpdate={handleUpdate}
        />
      ) : (
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
          Nenhuma opção criada.
        </Typography>
      )}
    </div>
  );
}